import { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import jwtDecode from 'jwt-decode'
import { Api } from 'src/servicos'
import { UsuarioPerfil } from 'src/tipos'
import { createContainer } from 'unstated-next'

import { useBarraSuperior } from '../barra-superior-layout'
import {
  normalizarPerfisJwt,
  recuperarRotaBasePorVisao,
  recuperarVisaoPorPerfis
} from './funcoes'
import { AuthContainer, Perfil, Visao, Usuario } from './tipos'

const chaveUsuario = '@ipgs/usuario'
const chavePerfil = '@ipgs/perfil'
const chaveUrlRedirecionamento = '@ipgs/urlRedirecionamento'

const useAuth = () => {
  const history = useHistory()
  const semRedirecionamento = useRef(false)
  const barraSuperior = useBarraSuperior()

  const recuperarItemDoArmazenamentoLocal = (chave: string) => {
    try {
      const valor = localStorage.getItem(chave)
      return valor ? JSON.parse(valor) : null
    } catch {
      return null
    }
  }

  const recuperarUsuario = () => recuperarItemDoArmazenamentoLocal(chaveUsuario)

  const recuperarPerfil = () => recuperarItemDoArmazenamentoLocal(chavePerfil)

  const recuperarUrlRedirecionamento = () =>
    recuperarItemDoArmazenamentoLocal(chaveUrlRedirecionamento)

  const [estado, definirEstado] = useState<{
    usuario: Usuario | null
    perfil: Perfil | null
  }>({
    usuario: recuperarUsuario(),
    perfil: recuperarPerfil()
  })
  const [visao, definirVisao] = useState<Visao>(
    recuperarVisaoPorPerfis(recuperarPerfil()?.perfis)
  )

  const obterPerfil = async (token: string): Promise<Perfil | undefined> => {
    try {
      const claims = jwtDecode<{
        sub: string
        email: string
        role: keyof typeof UsuarioPerfil | Array<keyof typeof UsuarioPerfil>
        impersonated?: string
      } & Record<'impersonator_id', string | undefined>>(token)

      const { sub: id, email, role, impersonated } = claims
      const impersonadorId = claims.impersonator_id

      return {
        id,
        email,
        // A claim 'role' vem como string quando o usuario tem um unico perfil
        // e como array quando tem varios; normalizamos para sempre ser array.
        perfis: normalizarPerfisJwt(role),
        // Persistido no perfil (localStorage) para a sessao de impersonate
        // sobreviver a um refresh da pagina.
        impersonando: impersonated === 'true',
        impersonadorId
      } as Perfil
    } catch (_) {
      // logout
      return undefined
    }
  }

  const recuperarAutenticacaoDoEstado = (): boolean => {
    const usuario = recuperarUsuario()

    if (!usuario) return false

    const perfil = recuperarPerfil()

    if (!perfil) return false

    definirEstado({
      usuario,
      perfil
    })

    return true
  }

  useEffect(() => {
    if (!semRedirecionamento.current) {
      const urlRedirecionamento = recuperarUrlRedirecionamento()
      if (estado.perfil && urlRedirecionamento) {
        const url = urlRedirecionamento?.url ?? '/'
        localStorage.removeItem(chaveUrlRedirecionamento)

        history.replace(url === '/entrar' ? '/' : url)
      } else if (estado.perfil && history.location.pathname === '/entrar') {
        history.replace('/')
      }
    } else {
      semRedirecionamento.current = false
    }
  }, [estado.perfil])

  const solicitarAutenticacao = async () => {
    localStorage.setItem(
      chaveUrlRedirecionamento,
      JSON.stringify({
        url: history.location.pathname
      })
    )

    if (recuperarAutenticacaoDoEstado()) return

    history.push('/entrar')
  }

  const atualizarUsuario = async (
    dados: { token: string },
    naoRedirecionar?: boolean
  ) => {
    semRedirecionamento.current = naoRedirecionar
    const perfil = await obterPerfil(dados.token)

    if (!perfil?.email) {
      throw new Error('Não foi possível obter o perfil do token')
    }

    // TODO: Rever se é adequado manter essa requisição aqui
    const response = await fetch(
      `${process.env.REACT_APP_URL_GATEWAY}/api/usuario/email/${encodeURIComponent(
        perfil.email
      )}`,
      {
        headers: {
          Authorization: `Bearer ${dados.token}`
        }
      }
    )

    if (!response.ok) {
      throw new Error(`Falha ao carregar usuário (${response.status})`)
    }

    const body = await response.json()
    const novoUsuario = body?.data

    if (!body?.success || !novoUsuario) {
      throw new Error('Resposta inválida ao carregar usuário')
    }

    // foto base64 do usuario/email estoura a quota do localStorage (~5MB)
    const { foto: _fotoIgnorada, ...usuarioSemFoto } = novoUsuario
    const usuario = { ...dados, ...usuarioSemFoto }

    localStorage.setItem(chaveUsuario, JSON.stringify(usuario))
    localStorage.setItem(chavePerfil, JSON.stringify(perfil))

    definirVisao(recuperarVisaoPorPerfis(perfil.perfis))
    definirEstado({
      usuario,
      perfil
    })
  }

  const sair = async (naoRedirecionar?: boolean) => {
    localStorage.removeItem(chaveUsuario)
    localStorage.removeItem(chavePerfil)
    localStorage.removeItem('@ipgs/responderDepoisPesquisa')
    barraSuperior.removerFoto()
    semRedirecionamento.current = false
    definirEstado({
      usuario: null,
      perfil: null
    })
    if (!naoRedirecionar) history.push('/')
  }

  const conferirUsuarioLogado = () => {
    try {
      const valor = localStorage.getItem(chavePerfil)
      const perfilUsuarioLogado = JSON.parse(valor)
      const email = perfilUsuarioLogado.email
      return email || null
    } catch {
      return null
    }
  }

  const autenticar = async (
    usuario: string,
    senha: string,
    naoRedirecionar?: boolean
  ): Promise<boolean> => {
    try {
      semRedirecionamento.current = naoRedirecionar ?? false

      const token = await Api.Entrar(usuario, senha)

      if (!token) {
        return false
      }

      const dadosUsuario = {
        token
      }

      await atualizarUsuario(dadosUsuario, naoRedirecionar ?? false)

      return true
    } catch {
      localStorage.removeItem(chaveUrlRedirecionamento)
      return false
    }
  }

  const alterarVisao = (visao: Visao) => definirVisao(visao)

  // Troca a identidade da sessao pela do usuario-alvo. Reaproveita
  // atualizarUsuario, que grava o novo token/perfil e faz o
  // ControladorInstanciasApis recriar a instancia do axios com o novo Bearer.
  const iniciarImpersonate = async (usuarioId: string): Promise<boolean> => {
    try {
      const token = await Api.IniciarImpersonate(usuarioId)

      if (!token) return false

      await atualizarUsuario({ token }, true)

      const perfil = await obterPerfil(token)
      history.push(recuperarRotaBasePorVisao(perfil?.perfis))

      return true
    } catch {
      toast('Não foi possível iniciar a sessão de impersonate.', {
        type: 'error'
      })
      return false
    }
  }

  // Encerra a sessao pedindo ao backend um token limpo do impersonador
  // (o token de impersonate carrega a claim impersonator_id).
  const encerrarImpersonate = async (): Promise<boolean> => {
    try {
      const token = await Api.SairDoImpersonate()

      if (!token) return false

      await atualizarUsuario({ token }, true)
      // Rota literal (evita import de 'src/rotas/sistema', que causa
      // dependencia circular: sistema.tsx -> pagina de impersonate ->
      // AutenticacaoContainer -> este arquivo).
      history.push('/sistema/impersonate')

      return true
    } catch {
      toast('Não foi possível encerrar a sessão de impersonate.', {
        type: 'error'
      })
      return false
    }
  }

  const validarPerfilUnico = (perfilVerificar: string) => {
    if (!perfilVerificar) return false

    const { perfis } = estado?.perfil

    if (perfis === null) {
      return false
    }

    if (Array.isArray(perfis)) {
      return perfis.every(p => p === perfilVerificar)
    }

    return perfis === perfilVerificar
  }

  return {
    solicitarAutenticacao,
    autenticar,
    sair,
    visao,
    alterarVisao,
    atualizarUsuario,
    validarPerfilUnico,
    recuperarPerfil,
    conferirUsuarioLogado,
    impersonando: estado.perfil?.impersonando === true,
    iniciarImpersonate,
    encerrarImpersonate,
    ...estado
  }
}

export default createContainer<AuthContainer>(useAuth)
